import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';

import { CommandResult } from '@app/_models/command/commandResult';
import { CommandResultWithValue } from '@app/_models/command/commandResultWithValue';
import { Pagination } from '@app/_models/pagination';
import { QueryColumnSort } from '@app/_models/settings/queryColumnSort';
import { TIMEOUT_TOKEN } from '@app/_providers/httpErrorInterceptor';
import { BaseApiService } from './baseApi.service';

export abstract class BaseTableDataService<DataType, IdType> extends BaseApiService {
  protected readonly http = inject(HttpClient);

  protected readonly queryTimeout?: number;

  baseTableDataUrl: string;

  constructor(controllerBaseUrl: string, tableDataBaseUrl?: string) {
    super(controllerBaseUrl);
    if (tableDataBaseUrl) {
      this.baseTableDataUrl = `${this.baseControllerUrl}/${tableDataBaseUrl}`;
    } else {
      this.baseTableDataUrl = `${this.baseControllerUrl}`;
    }
  }

  // Overridable so a service can query a differently gated list endpoint while get/update/create/delete keep
  // operating on the base resource (see PendingUserContractForInsuranceService).
  protected get queryUrl(): string {
    return `${this.baseTableDataUrl}/query`;
  }

  query(
    filter: string,
    pageIndex: number,
    pageSize: number,
    sort?: QueryColumnSort[],
    additionalParams?: HttpParams,
  ): Observable<Pagination<DataType>> {
    let params = new HttpParams().set('Filter', filter).set('PageIndex', pageIndex).set('PageSize', pageSize);

    if (sort) {
      const columnSort = [];

      for (const queryColumnSort of sort) {
        columnSort.push(`${queryColumnSort.column}:${queryColumnSort.direction}`);
      }

      params = params.set('Sort', columnSort.join('|'));
    }

    if (additionalParams) {
      for (const key of additionalParams.keys()) {
        const value = additionalParams?.get(key);

        if (value) {
          params = params.set(key, value);
        }
      }
    }

    const context =
      this.queryTimeout !== undefined ? new HttpContext().set(TIMEOUT_TOKEN, this.queryTimeout) : undefined;

    return this.http.get<Pagination<DataType>>(this.queryUrl, { params, context });
  }

  get(id: IdType, additionalParams?: HttpParams): Observable<DataType> {
    return this.http.get<DataType>(`${this.baseTableDataUrl}/${id}`, { params: additionalParams });
  }

  update(data: DataType, additionalParams?: HttpParams): Observable<CommandResult> {
    return this.http.put<CommandResult>(this.baseTableDataUrl, data, { params: additionalParams });
  }

  updateMultiple(data: DataType[], additionalParams?: HttpParams): Observable<CommandResult> {
    return this.http.put<CommandResult>(this.baseTableDataUrl + '/updateMultiple', data, { params: additionalParams });
  }

  create(data: DataType, additionalParams?: HttpParams): Observable<CommandResultWithValue<IdType>> {
    return this.http.post<CommandResultWithValue<IdType>>(this.baseTableDataUrl, data, { params: additionalParams });
  }

  delete(id: IdType, additionalParams?: HttpParams): Observable<CommandResult> {
    return this.http.delete<CommandResult>(`${this.baseTableDataUrl}/${id}`, { params: additionalParams });
  }
}
